import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const fmtMoney = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

const RANGES = [
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "last_30", label: "Last 30 Days" },
  { key: "last_90", label: "Last 90 Days" },
  { key: "ytd", label: "This Year" },
  { key: "all", label: "All Time" },
];

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-slate-200 rounded-md bg-white p-5" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">{label}</div>
      <div className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}

/** Minimal SVG bar chart — no dependency added. */
function CommissionBars({ data }) {
  const max = useMemo(() => Math.max(0.01, ...data.map((d) => d.commission)), [data]);
  if (!data.length) return <div data-testid="performance-chart" className="text-sm text-slate-500 text-center py-10">No activity in this range.</div>;
  return (
    <div className="w-full overflow-x-auto" data-testid="performance-chart">
      <svg viewBox={`0 0 ${Math.max(300, data.length * 34)} 160`} className="w-full h-40" preserveAspectRatio="none">
        {data.map((d, i) => {
          const x = i * 34 + 8;
          const h = Math.max(2, (d.commission / max) * 130);
          return (
            <g key={d.date}>
              <rect x={x} y={150 - h} width={20} height={h} rx="2" className="fill-blue-500" />
              <title>{`${d.date} · ${fmtMoney(d.commission)} commission · ${d.orders} orders`}</title>
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] font-mono text-slate-400 mt-1">
        <span>{data[0].date}</span>
        <span>{data[data.length - 1].date}</span>
      </div>
    </div>
  );
}

export default function AffiliatePerformance() {
  const [range, setRange] = useState("this_month");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/me/affiliate/performance?range=${range}`).then((r) => setData(r.data)).catch(() => setData({ clicks: 0, orders: 0, revenue: 0, commission: 0, conversion_rate: 0, daily: [] }));
  }, [range]);

  return (
    <div className="space-y-6" data-testid="affiliate-performance-page">
      <div className="flex flex-wrap gap-2">
        {RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            data-testid={`range-${r.key}`}
            className={`px-3 py-1.5 rounded text-xs font-mono uppercase tracking-widest border ${
              range === r.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {!data && <div className="text-slate-400 font-mono text-xs uppercase tracking-widest">Loading…</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <StatCard label="Clicks" value={data.clicks} testid="perf-clicks" />
            <StatCard label="Orders" value={data.orders} testid="perf-orders" />
            <StatCard label="Conversion" value={`${data.conversion_rate}%`} testid="perf-conversion" />
            <StatCard label="Revenue" value={fmtMoney(data.revenue)} testid="perf-revenue" />
            <StatCard label="Commission" value={fmtMoney(data.commission)} testid="perf-commission" />
          </div>
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="eyebrow mb-3">Commission over time</div>
            <CommissionBars data={data.daily} />
          </div>
        </>
      )}
    </div>
  );
}
